import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { EnergyTab as EnergyTabUI } from '@popety-io/ui'
import { useClearMapSection } from '../hooks'

interface EnergyTabProps {
  estimateData: any
}

export const EnergyTab: React.FC<EnergyTabProps> = ({ estimateData }) => {
  const { t } = useTranslation('energy')

  // Clear map section data when viewing energy tab
  useClearMapSection()

  // Match main app: get building from buildings[0]
  const building = estimateData?.value?.buildings?.[0]

  // Merge saved energy data into building (same as main app)
  const enhancedBuilding = useMemo(() => {
    if (!building) return null

    const savedEnergy = estimateData?.value_edited?.energy
    if (!savedEnergy) return building

    // Enhance buildings_administrative with saved timestamp fields
    const enhancedBuildingsAdministrative =
      building.buildings_administrative?.map((admin: any) => ({
        ...admin,
        last_update_timestamp_heater:
          admin.last_update_timestamp_heater ||
          savedEnergy.lastUpdateTimestampHeater,
        last_update_timestamp_water_heater:
          admin.last_update_timestamp_water_heater ||
          savedEnergy.lastUpdateTimestampWaterHeater,
        last_update_timestamp_additional_heater:
          admin.last_update_timestamp_additional_heater ||
          savedEnergy.lastUpdateTimestampAdditionalHeater,
        last_update_timestamp_additional_water_heater:
          admin.last_update_timestamp_additional_water_heater ||
          savedEnergy.lastUpdateTimestampAdditionalWaterHeater,
        heater_distribution:
          admin.heater_distribution || savedEnergy.heaterDistribution,
      })) || []

    return {
      ...building,
      buildings_administrative: enhancedBuildingsAdministrative,
      heat_consumption_index:
        building.heat_consumption_index || savedEnergy.heatConsumptionIndex,
      energy_reference_area:
        building.energy_reference_area || savedEnergy.energyReferenceArea,
      minergie: building.minergie || savedEnergy.minergie,
      solar_area: building.solar_area || savedEnergy.solarArea,
      solar_area_best: building.solar_area_best || savedEnergy.solarAreaBest,
      solar_electrical_efficiency:
        building.solar_electrical_efficiency ||
        savedEnergy.solarElectricalEfficiency,
      solar_electrical_efficiency_best:
        building.solar_electrical_efficiency_best ||
        savedEnergy.solarElectricalEfficiencyBest,
      solar_installed_power:
        building.solar_installed_power || savedEnergy.solarInstalledPower,
      photovoltaic_history:
        building.photovoltaic_history || savedEnergy.photovoltaicHistory,
    }
  }, [building, estimateData?.value_edited?.energy])

  return (
    <EnergyTabUI
      building={enhancedBuilding}
      loading={!estimateData}
      error={
        !enhancedBuilding
          ? t(
              'noBuildingData',
              'No building data available for energy analysis'
            )
          : null
      }
      t={t}
    />
  )
}
